import React, { useState } from "react";
import { TrendingUp } from "lucide-react";
import GraficButton from "./RentabilityGrafic/GraficButton";
import RentabilityGrafic from "./RentabilityGrafic/RentabilityGrafic";
import RevenueTableList from "./RevenueTableList";
import RevenueInsightsSummary from "./RevenueInsightsSummary";
import DashboardCard from "../components/DashboardCard";
import DashboardCardSkeleton from "../components/DashboardCardSkeleton";
import DashboardEmptyState from "../components/DashboardEmptyState";
import DashboardPeriodFilter from "../components/DashboardPeriodFilter";
import KenloPropertyFilter from "../components/KenloPropertyFilter";
import {
  DashboardPeriodPreset,
  IRentByDate,
  IRevenueInsights,
} from "../types/ownerDashboard.types";
import { KenloPropertyOption } from "../utils/getKenloPropertyOptions";

interface IRents {
  smDown: boolean;
  lgDown?: boolean;
  chartData: IRentByDate[];
  revenueInsights?: IRevenueInsights;
  chartPeriod: DashboardPeriodPreset;
  chartZoom: number | "all";
  selectedKenloPropertyId: string | null;
  propertyOptions: KenloPropertyOption[];
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  onChartPeriodChange: (period: DashboardPeriodPreset) => void;
  onChartZoomChange: (zoom: number | "all") => void;
  onPropertyChange: (kenloPropertyId: string | null) => void;
}

export default function RentabilityChart({
  chartData = [],
  revenueInsights,
  chartPeriod,
  chartZoom,
  selectedKenloPropertyId,
  propertyOptions,
  smDown,
  lgDown = false,
  isLoading = false,
  isError = false,
  onRetry,
  onChartPeriodChange,
  onChartZoomChange,
  onPropertyChange,
}: IRents) {
  const [showChart, setShowChart] = useState(true);

  const bills = chartData.map((item) => ({
    date: item.date,
    final_value: item.final_value,
    cumulative_value: item.cumulative_value,
  }));

  const padding = smDown ? "0.875rem" : lgDown ? "1rem" : "1.125rem";

  if (isLoading) {
    return <DashboardCardSkeleton minHeight="min-h-[18rem]" lines={5} />;
  }

  if (isError) {
    return (
      <DashboardEmptyState
        align="center"
        title="Histórico de receita indisponível"
        description="Estamos carregando seu histórico de repasses. Isso pode levar mais tempo na primeira visita."
        action={
          onRetry ? (
            <button
              type="button"
              onClick={onRetry}
              className="rounded-full bg-[#6ea8ff] px-4 py-2 text-xs font-bold text-[#0d1117] transition-opacity hover:opacity-90"
            >
              Tentar novamente
            </button>
          ) : undefined
        }
        className="min-h-[18rem] justify-center"
      />
    );
  }

  return (
    <DashboardCard padding={padding} className="flex h-full min-h-0 flex-col">
      <div className="flex min-h-0 w-full flex-1 flex-col gap-3">
        <div className="flex w-full flex-col gap-3">
          <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 shrink-0 text-[#6ea8ff]" />
                <h2 className="text-base font-bold text-[#f0f6fc] sm:text-lg">Sua receita</h2>
              </div>
              <div className="mt-2">
                <RevenueInsightsSummary revenueInsights={revenueInsights} />
              </div>
            </div>
            <GraficButton onClick={() => setShowChart(!showChart)}>
              {showChart ? "Ver tabela" : "Ver gráfico"}
            </GraficButton>
          </div>

          <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2">
            <DashboardPeriodFilter
              value={chartPeriod}
              onChange={onChartPeriodChange}
              label="Período do gráfico"
              compact
            />
            <KenloPropertyFilter
              properties={propertyOptions}
              selectedKenloPropertyId={selectedKenloPropertyId}
              onSelectProperty={onPropertyChange}
            />
          </div>
        </div>

        {showChart ? (
          <div className="min-h-0 flex-1">
            <RentabilityGrafic
              data={bills}
              chartZoom={chartZoom}
              onChartZoomChange={onChartZoomChange}
            />
          </div>
        ) : (
          <RevenueTableList bills={bills} />
        )}
      </div>
    </DashboardCard>
  );
}
